import React, { useState, useImperativeHandle, forwardRef } from "react";
import { useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { addCreatives, addDimensions } from "../../../actions/app/creatives";
import AssetUploader from "./AssetUploader";
import uuid from "uuid";

// Validator module for external IDs
export function isValidExternalId(filename, delimiter = "_") {
  if (!filename || typeof filename !== "string") return false;

  // Need at least 10 + 4 + 1 delimiter = 15 chars
  if (filename.length < 15) return false;

  // delimiter should be at index 14 (0-based)
  if (filename[14] !== delimiter) return false;

  const firstTen = filename.slice(0, 10);
  const lastFour = filename.slice(10, 14);

  // 10 alphanumeric characters followed by 4 digits
  const alphaNumRegex = /^[A-Za-z0-9]{10}$/;
  const digitsRegex = /^\d{4}$/;

  return alphaNumRegex.test(firstTen) && digitsRegex.test(lastFour);
}

export default forwardRef(
  (
    {
      isThumbnailEnabled,
      filetype,
      creativeTypes,
      selectedCreatives,
      setSelectedCreatives,
      placements,
      canChooseExisting = true,
      uploadLimit = 20
    },
    ref
  ) => {
    let dispatch = useDispatch();
    const location = useLocation();
    let [snackbarState, setSnackbarState] = useState(false);
    let creativeState = useSelector((state) => state.creatives);
    let assetsById = creativeState.byId;
    let assetIds = creativeState.allIds;

    const handleDrop = (acceptedFiles) => {
      let newFiles = acceptedFiles.map((file) => {
        let id = uuid.v4(); //generate id
        let reader = new FileReader();

        reader.onload = function (e) {
          let img = new Image();
          img.src = e.target.result; //create Image with file as src

          img.onload = function () {
            let dimensions = this.width + " x " + this.height; //grab dimensions
            dispatch(addDimensions(id, dimensions)); //update dimensions property on creative
          };
        };

        reader.readAsDataURL(file); //read file

        let externalId = "";

        if (isValidExternalId(file.name)) externalId = file.name.slice(0, 14);

        return {
          id,
          externalId,
          file: file,
          preview: URL.createObjectURL(file),
          weight: 0,
          createdInPreloader: location.pathname.includes("preloader")
            ? true
            : false,
          taxonomy: { externalId }
        };
      });

      if (newFiles.length + assetIds.length <= uploadLimit) {
        dispatch(addCreatives(newFiles));
      } else {
        setSnackbarState(true);
      }
    };

    useImperativeHandle(ref, () => ({
      validate() {
        if (assetIds.length === 0) {
          return {
            hasErrors: true,
            message: "You need to add at least one asset."
          };
        }

        return {
          hasErrors: false,
          message: ""
        };
      }
    }));

    return (
      <AssetUploader
        assetsById={assetsById}
        assetIds={assetIds}
        weightDistribution={creativeState.weightDistribution}
        rotationType={creativeState.rotationType}
        handleDrop={handleDrop}
        snackbarOpen={snackbarState}
        setSnackbarState={setSnackbarState}
        isThumbnailEnabled={isThumbnailEnabled}
        filetype={filetype}
        creativeTypes={creativeTypes}
        selectedCreatives={selectedCreatives}
        setSelectedCreatives={setSelectedCreatives}
        placements={placements}
        canChooseExisting={canChooseExisting}
        uploadLimit={uploadLimit}
      />
    );
  }
);
